<template>
    <div>
        <p v-if="error" class="text-danger">{{errorMsg}}</p>
        <div class="row">
            <div class="col-12">
                <div class=" bg-white border rounded shadow-sm mt-3 p-3 mb-4">
                    <div>
                        <h5 class="d-inline-block text-info">Team Members</h5>
                        <base-popover-icon
                            title="Sourcing Event Team Members"
                            content="These are the team members internal to your company who have access to this specific sourcing event.  Each team members' role affects what they can do with this event.  See the definitions at the bottom of the page."
                            placement="rightbottom"
                            class="ml-2"
                        ></base-popover-icon>
                    </div>
                    
                    <b-table
                        responsive
                        :busy="loadingList"
                        :items="rfxTenantUsers"
                        :fields="rfxTenantUserCols"
                        primary-key="id"
                        class="no-top-border"
                        tbody-class="td-align-middle"
                    >
                        <template #table-busy>
                            <busy-spinner text="Loading Team..."></busy-spinner>
                        </template>
                        <template #cell(tenant_user_name)="data">
                            {{ data.value }}
                            <div v-if="data.item.is_sourcing_owner" class="mt-1 text-uppercase small text-accent">
                                Event Owner
                            </div>
                        </template>
                        <template #cell(role)="data">
                            <!-- <div>{{ rfxTenantUserRoles[data.value].title }}</div>
                            <div class="text-muted small">
                                {{ rfxTenantUserRoles[data.value].description }}
                            </div> -->
                            <div class="d-flex align-items-center">
                                <b-form-select
                                    v-model="data.item.role"
                                    :options="availableRolesForTenantUser(data.item.tenant_user)"
                                    :disabled="data.item.is_sourcing_owner || isReadOnly"
                                    text-field="title"
                                    @change="handleRoleChange(data.item)"
                                >
                                </b-form-select>
                                <div class="pl-1" style="min-width: 40px">
                                    <b-spinner v-if="savingRoleId === data.item.id" small></b-spinner>
                                </div>
                            </div>
                        </template>

                        <template #cell(actions)="data">
                            <b-button 
                                variant="minimal-danger" 
                                size="sm"
                                :disabled="data.item.is_sourcing_owner || isReadOnly"
                                @click="handleRemove(data.item)"
                            >Remove</b-button>
                            <!-- The old conditional: :disabled="currentUserInfo.tenant_user_id === data.item.tenant_user.id" -->
                        </template>
                    
                    </b-table>
                    <div class="text-left">
                        <b-button
                            variant="soft-primary"
                            :disabled="isReadOnly"
                            @click="showAddModal = true"
                        >
                            Add Team Member
                        </b-button>
                    </div>
                </div>
                
            </div>

            <div class="col-12">
                <h6 class="text-info mb-2">Role Definitions</h6>
                <dl class="row">
                    <template v-for="role in visibleRfxTenantUserRoles">
                        <dt class="col-sm-2" :key="'title-' + role.value">{{role.title}}</dt>
                        <dd class="col-sm-10" :key="'description-' + role.value">{{role.description}}</dd>
                    </template>
                </dl>

            </div>

        </div>

        <b-modal
            v-model="showAddModal"
            title="Add Team Member"
            centered
            hide-footer
            @hidden="addModalHidden"
        >
            <h6>Step 1:  Select User:</h6>
            <multiselect
                v-model="newTenantUser" 
                :options="filteredTenantUsers"
                placeholder="Team member..."
                label="full_name"
                track-by="id"
                class="mb-4"
                @select="newRole = ''"
            >
                <span slot="noOptions">No other team members available.</span>
            </multiselect>
            <h6>Step 2:  Select Role:</h6>
            <b-form-select
                v-model="newRole"
                :options="availableRolesNewAdd"
                :disabled="!newTenantUser || !newTenantUser.role || isReadOnly"
                text-field="title"
                class="mb-4"
                size="lg"
            >
                <template v-slot:first>
                    <b-form-select-option value="" disabled>Select a role...</b-form-select-option>
                </template>
            </b-form-select>
            <b-button
                variant="success"
                :disabled="!newTenantUser || !newRole || savingAdd || isReadOnly"
                class="min-width-150"
                @click="addRfxTenantUser"
            >
                <b-spinner v-if="savingAdd" key="savingAdd" small></b-spinner>
                <span v-else key="notSavingAdd">Add Team Member</span>
            </b-button>
        </b-modal>
    
    </div>
</template>

<script>
import { suppliosApi } from '../js/api-service';
import Multiselect from 'vue-multiselect';
import cloneDeep from 'lodash/cloneDeep';
import { currentUserInfoMixin } from '../js/custom-vue-mixins';

export default {
    name: 'RfxManageTeam',
    components: {
        Multiselect,
    },
    mixins: [currentUserInfoMixin],
    props: {
        rfxPk: {
            type: [String, Number],
            required: true,
        },
        isReadOnly: {
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            errorMsg: '',
            error: false,
            loadingUsers: false,  
            loadingList: false,  // when loading data for table
            savingAdd: false,  // when saving new team member
            savingRoleId: null,  // set this to RfxTenantUser ID when updating that one
            allTenantUsers: [],  // put list here from API
            rfxTenantUsers: [], // 
            showAddModal: false,  // controls modal
            newTenantUser: null,  // TenantUser (full object) to add, from multiselect
            newRole: '',  // ROLE for new team member to add
            rfxTenantUserCols: [
                {
                    key: 'tenant_user_name',
                    label: 'Person',
                    formatter: (value, key, item) => {
                        return item.tenant_user.full_name;
                    },
                    tdClass: 'font-weight-bold',
                },
                {
                    key: 'email',
                    label: 'Email',
                    formatter: (value, key, item) => {
                        return item.tenant_user.email;
                    },
                },
                {
                    key: 'job_title',
                    label: 'Title',
                    formatter: (value, key, item) => {
                        return item.tenant_user.job_title;
                    }
                },
                {
                    key: 'work_location',
                    label: 'Work Location',
                    formatter: (value, key, item) => {
                        return item.tenant_user.work_location;
                    }
                },
                {
                    key: 'role',
                    label: 'Role',
                },
                {
                    key: 'actions',
                    label: 'Actions',
                }
            ],
            rfxTenantUserRoles: [
                // list of RfxTenantUser roles w/ db name, title, description for display
                // hard-coding these here for now, could get from API later
                // key/values of DB-name / friendly name just for display
                {
                    value: 'admin',  // don't change this -- its the value used by backend
                    title: 'Event Admin',
                    description: 'Full access & control over the event.',
                    disabled: false,
                    hide: false,
                },
                {
                    value: 'decision',  // don't change this -- its the value used by backend
                    title: 'Collaborator',
                    description: 'Can view vendor responses and message with vendors, but cannot manage the event or invite suppliers.',
                    disabled: false,
                    hide: false,
                },
                {
                    value: 'requester',  // don't change this -- its the value used by backend
                    title: 'Requester',
                    description: 'Can contribute content for the event, but cannot see responses or message with vendors.',
                    disabled: true,
                    hide: true,  // hide from front-end, hiding description and in new select box
                }
            ]
        }
    },
    computed: {
        // has currentUserInfo from mixin
        rfxTenantUserToAdd: function() {
            // the object we'll POST to create new RfxTenantUser
            // only need role (string) and TenantUser ID
            return {
                role: this.newRole,
                tenant_user: this.newTenantUser ? this.newTenantUser.id : null,
            }
        },
        filteredTenantUsers: function() {
            // filters out any tenant users that are already on the list
            let selectedTenantUsers = this.rfxTenantUsers.map(rtu => {
                return rtu.tenant_user.id;
            });  // returns IDs of TenantUsers already on the list, now filter those out
            return this.allTenantUsers.filter(tu => {
                return !selectedTenantUsers.includes(tu.id);
            });
        },
        visibleRfxTenantUserRoles: function() {
            // just return those for which .hide is not true
            return this.rfxTenantUserRoles.filter(rturole => !rturole.hide);
        },
        availableRolesNewAdd: function() {
            // used to control available roles in the MODAL for add new team member only (there is a separate method for getting roles available to each existing team member)
            // takes this.visibleRfxTenantUserRoles and adds 'disabled' key based on which TU is selected... disables admin for people who shouldn't have it
            return this.availableRolesForTenantUser(this.newTenantUser);
        },
    },
    mounted: function() {
        this.getAllTenantUsers();
        this.getRfxTenantUsers();
    },
    methods: {
        saveAll: function() {
            // called by parent as part of setup wizard
            // must return Promise that resolves to Boolean based on success or not
            // for now, since there is no 'save' method (just add) we'll just return a resolved promise
            //return Promise.resolve(true);
            this.showSaveSuccess();
            return {
                statusPromise: Promise.resolve(true),
                successMessage: 'Team members saved',
            };
        },
        okToContinue: function(tabChangeEvent) {
            // PUBLIC METHOD CALLED FROM PARENT - takes tabChangeEvent as arg, return Promise that resolves to boolean where true = can continue
            console.log('from component okToContinue, tabChangeEvent is below:');
            console.log(tabChangeEvent);
            return Promise.resolve(true);  // change later
        },
        showSaveSuccess: function(msg) {
            // separating out save success message toast for DRY
            let successMsg = msg ? msg : 'Team settings saved';
            this.$toast.success(successMsg);
        },
        // PUBLIC METHODS ABOVE, private methods below
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        getAllTenantUsers: function() {
            this.loadingUsers = true;
            suppliosApi.orgData.internalUserList()
            .then(response => {
                console.log('got tenant user list, full response below');
                console.log(response);
                this.allTenantUsers = response.data;
            })
            .catch(error => this.handleError(error, 'Error while getting user list'))
            .finally(() => this.loadingUsers = false)
        },
        getRfxTenantUsers: function() {
            this.loadingList = true;
            suppliosApi.rfx.getRfxTenantUsers(this.rfxPk)
            .then(response => {
                console.log('got list of rfxTenantUsers, response below');
                console.log(response);
                this.rfxTenantUsers = response.data;
            })
            .catch(error => this.handleError(error, 'Error while getting list of existing RFX Tenant Users'))
            .finally(() => this.loadingList = false)
        },
        /*
        removeRfxTenantUser: function(rfxTenantUserObj) {
            console.log(rfxTenantUserObj);
            this.loadingList = true;
            suppliosApi.rfx.removeRfxTenantUser(rfxTenantUserObj.id)
            .then(response => {
                console.log('removed tenant user from rfx, response below');
                console.log(response);
                this.getRfxTenantUsers();  // reload the list
            })
            .catch(error => this.handleError(error, 'Error while removing person from event'))
            // no finally, reloading list will clear it
        },*/
        handleRoleChange: function(rfxTenantUserObj) {
            // object passed in is AFTER making change to role, so get new role from that
            this.savingRoleId = rfxTenantUserObj.id;  // set this to the ID that should be spinning
            const dataToSend = {
                'id': rfxTenantUserObj.id,
                'role': rfxTenantUserObj.role,
            };
            suppliosApi.rfx.changeRfxTenantUserRole(this.rfxPk, dataToSend)
            .then(response => {
                console.log('changed role - full response below');
                console.log(response);
                // no need to refresh, we mutated local data and posted the small change
            })
            .catch(error => this.handleError(error, 'Error while changing users role'))
            .finally(() => this.savingRoleId = null)  // set back to null
        },
        addRfxTenantUser: function() {
            // add new team member to the list
            this.savingAdd = true;
            const postData = {...this.rfxTenantUserToAdd};  
            console.log(postData);
            suppliosApi.rfx.addRfxTenantUser(this.rfxPk, postData)
            .then(response => {
                console.log('added new team member, response below');
                console.log(response);
                this.showAddModal = false;  // close the modal, show toast
                this.$toast.success('New team member has been added');
                this.getRfxTenantUsers();  // reload the list
            })
            .catch(error => this.handleError(error, 'Error while adding new team member'))
            .finally(() => this.savingAdd = false)
        },
        handleRemove: function(removeObj) {
            this.$bvModal.msgBoxConfirm('Please confirm that you want to remove access for this team member.', {
                title: 'Are you sure?',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Yes, Remove',
                cancelTitle: 'No, Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            })
            .then(value => {
                // value resolves to true if OK is clicked, false if cancel clicked, or null if modal closed
                if (value === true) {
                    this.performRemove(removeObj);
                    console.log("From delete confirm modal, we just called performRemove");
                }
            })
            .catch(error => {
                this.handleError(error, 'There was an error during handleRemove.');
            })
        },
        performRemove: function(removeObj) {
            // actually does the remove/delete via API
            this.loadingList = true;
            const removeData = {  // our endpoint needs this
                rfx_tenant_user_id: removeObj.id,
            }
            suppliosApi.rfx.removeRfxTenantUser(this.rfxPk, removeData)
            .then(response => {
                console.log('removed object via API, full response below');
                console.log(response);
                this.getRfxTenantUsers();  // refresh the list
            })
            .catch(error => this.handleError(error, 'Error while removing team member'))
            // no need for finally here... refreshing list will clear loading
        },
        lookupRfxTenantRole: function(roleValue) {
            return this.visibleRfxTenantUserRoles.find(role => role.value === roleValue);
            // returns complete object
        },
        addModalHidden: function() {  // reset inputs for add new form
            this.newTenantUser = null;
            this.newRole = '';
        },
        availableRolesForTenantUser: function(tenantUserObj) {
            // takes this.visibleRfxTenantUserRoles and adds 'disabled' key based on which TU is selected... disables admin for people who shouldn't have it
            if (!tenantUserObj || !tenantUserObj.role_expanded) return [];
            let canBeAdmin = tenantUserObj.role_expanded.is_paid_seat;
            if (canBeAdmin) {
                return this.visibleRfxTenantUserRoles;
            } else {
                let modifiedRoles = cloneDeep(this.visibleRfxTenantUserRoles);
                let adminRole = modifiedRoles.find(role => role.value === 'admin');
                adminRole.disabled = true;
                return modifiedRoles;
                // return this.visibleRfxTenantUserRoles.filter(role => role.value !== 'admin');  // disable 'admin' role
            }
        },
    }
}
</script>

<style>

</style>
